import { type ActivityListItem } from '@utazzo/shared';
import { motion } from 'framer-motion';
import { Image as ImageIcon, MapPin, Star } from 'lucide-react';
import React from 'react';

import { LegacyBadge } from '@/components/legacy-badge';
import { fadeUp } from '@/lib/motion';

/**
 * Activity card - mirrors HotelCard's structural rhythm: hero (photo when
 * available, else photo-placeholder), title + legacy, city/country row,
 * category strip, and a status dot + INR price footer. Identity comes from
 * real data, not decoration.
 */

interface ActivityCardProps {
  activity: ActivityListItem;
  onClick?: (activity: ActivityListItem) => void;
}

const STATUS_TONE: Record<ActivityListItem['status'], { dot: string; label: string }> = {
  ACTIVE: { dot: 'bg-emerald-500', label: 'Active' },
  DRAFT: { dot: 'bg-amber-500', label: 'Draft' },
  PENDING_APPROVAL: { dot: 'bg-amber-500', label: 'Pending' },
  INACTIVE: { dot: 'bg-zinc-400', label: 'Inactive' },
  ARCHIVED: { dot: 'bg-zinc-400', label: 'Archived' },
};

const fmtInr = (amount: number): string =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);

export const ActivityCard = ({ activity, onClick }: ActivityCardProps): React.JSX.Element => {
  const status = STATUS_TONE[activity.status];
  return (
    <motion.button
      type="button"
      onClick={() => onClick?.(activity)}
      variants={fadeUp}
      className={
        'group relative flex flex-col overflow-hidden rounded-lg border border-zinc-200 bg-white text-left ' +
        'transition-[border-color,background-color] duration-150 ' +
        'hover:border-zinc-300 hover:bg-zinc-50/30 ' +
        'focus-visible:border-zinc-400 focus-visible:ring-2 focus-visible:ring-zinc-300 focus-visible:ring-offset-2 focus-visible:outline-none'
      }
    >
      {/* Hero */}
      <div className="relative flex h-28 items-center justify-center overflow-hidden border-b border-zinc-100 bg-zinc-50">
        {activity.imageUrl ? (
          <img
            src={activity.imageUrl}
            alt={activity.name}
            className="h-full w-full object-cover"
            loading="lazy"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <ImageIcon className="h-6 w-6 text-zinc-300" strokeWidth={1.5} aria-hidden />
        )}
        {activity.rating > 0 && (
          <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded bg-white px-1.5 py-0.5 shadow-sm ring-1 ring-zinc-200/80">
            <Star className="h-3 w-3 fill-amber-400 text-amber-400" strokeWidth={0} />
            <span className="text-[10px] font-semibold text-zinc-700 tabular-nums">
              {activity.rating}
            </span>
          </span>
        )}
        {activity.variantCount > 1 && (
          <span className="absolute top-2 right-2 rounded bg-white px-1.5 py-0.5 text-[10px] font-medium tracking-wide text-zinc-600 ring-1 ring-zinc-200/80">
            {activity.variantCount} variants
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-2 p-4">
        <div className="flex items-start gap-2">
          <h3 className="line-clamp-1 flex-1 text-sm leading-snug font-semibold tracking-tight text-zinc-900">
            {activity.name}
          </h3>
          <LegacyBadge legacyId={activity.legacyId} />
        </div>

        <div className="flex flex-col gap-0.5">
          <span className="inline-flex items-center gap-1 text-xs text-zinc-600">
            <MapPin className="h-3 w-3 shrink-0 text-zinc-400" strokeWidth={2} aria-hidden />
            <span className="truncate">
              {activity.cityName}
              {activity.countryName ? `, ${activity.countryName}` : ''}
            </span>
          </span>
          <span className="truncate pl-4 text-xs text-zinc-500">
            {activity.category}
            {activity.subCategory ? ` · ${activity.subCategory}` : ''}
          </span>
        </div>

        {/* Meta footer - status dot + vendor on the left, INR price on the right. */}
        <div className="mt-auto flex items-center gap-3 border-t border-zinc-100 pt-3 text-xs">
          <span className="inline-flex items-center gap-1.5 text-zinc-600">
            <span className={'h-1.5 w-1.5 rounded-full ' + status.dot} aria-hidden />
            {status.label}
          </span>
          {activity.vendorSupplierName && (
            <>
              <span className="text-zinc-300" aria-hidden>
                ·
              </span>
              <span
                className="max-w-[110px] truncate text-zinc-500"
                title={activity.vendorSupplierName}
              >
                {activity.vendorSupplierName}
              </span>
            </>
          )}
          <span className="flex-1" />
          {activity.baseAdultPriceInr !== null ? (
            <span className="text-right">
              <span className="block text-[9px] font-medium tracking-wider text-zinc-400 uppercase">
                From
              </span>
              <span className="block text-sm font-semibold text-zinc-900 tabular-nums">
                {fmtInr(activity.baseAdultPriceInr)}
              </span>
            </span>
          ) : (
            <span className="text-[10px] text-zinc-400">No price</span>
          )}
        </div>
      </div>
    </motion.button>
  );
};
